///DE-3813 IF-DAS | DATACP | TMS (Adobe Launch) Load Performance | Development (Interim MVP)
// This hook is used to check if the Adobe Launch script has loaded and if the window._satellite object exists
// It also sends a health check to the personalization service with the results of the test

import { useEffect, useRef, useState } from "react";
import { publicConfig } from "@lululemon/ecom-common/src/util/public-config";
import { useRouter } from "next/router";

// Utility function to check if a URL contains all required substrings
const urlContainsAllElements = (url, elements) => {
  return elements.every((element) => url.includes(element));
};

function checkResourceLoadTime(resource) {
  const loadTime = resource?.responseEnd - resource?.startTime;

  if (typeof loadTime !== "number" || isNaN(loadTime)) {
    return 0;
  }

  return Math.floor(loadTime);
}

const useWithResourceReadyTest = (props) => {
  const router = useRouter();

  const checkIntervalMs = 500; // 1 second
  const timeoutDurationMs = 3000; // 3 seconds
  const requiredElements = ["https://assets.adobedtm.com/", "/launch-"]; // Substrings to check for in the script URL

  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptLoadedTime, setScriptLoadedTime] = useState(0);
  const [scriptLoadedStatus, setScriptLoadedStatus] = useState(0);
  const [satelliteExists, setSatelliteExists] = useState(false);
  const [timeoutReached, setTimeoutReached] = useState(false);
  const [pagePath, setPagePath] = useState("");

  const {
    launchLoadError: launchLoadErrorEvent,
    launchScriptLoaded: launchScriptLoadedEvent,
  } = useLaunchEventListeners(timeoutDurationMs);

  // Reset the state on every route change
  useEffect(() => {
    const handleRouteChange = (e) => {
      // Reset all states when a page changes
      setScriptLoaded(false);
      setScriptLoadedTime(0);
      setScriptLoadedStatus(0);
      setSatelliteExists(false);
      setTimeoutReached(false);
      setPagePath(e);
    };

    router.events.on("routeChangeStart", handleRouteChange);

    // Cleanup event listener on unmount
    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      // Check if the script has loaded
      const resources = window.performance.getEntriesByType("resource");
      //when script is blocked by fingerprinting protection, it will not be loaded
      const scriptLoadedNow = resources.find((entry) =>
        urlContainsAllElements(entry.name, requiredElements)
      );

      if (scriptLoadedNow) {
        const loadTime = checkResourceLoadTime(scriptLoadedNow);
        setScriptLoadedTime(loadTime);
        setScriptLoadedStatus(scriptLoadedNow?.responseStatus);
        if (loadTime !== 0) {
          setScriptLoaded(true);
        }
      }

      // Check if window._satellite exists
      const satelliteExistsNow =
        window._satellite && typeof window._satellite === "object";
      if (satelliteExistsNow) setSatelliteExists(true);
    }, checkIntervalMs);

    const timeoutId = setTimeout(() => {
      setTimeoutReached(true); // Flag to indicate timeout has been reached
      clearInterval(intervalId);
    }, timeoutDurationMs);

    // Cleanup on component unmount
    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [pagePath]);

  useEffect(() => {
    if (timeoutReached) {
      // Send health check only after timeout has been reached
      sendHealthCheck({
        scriptLoaded,
        satelliteExists,
        scriptLoadedTime,
        scriptLoadedStatus,
        launchLoadErrorEvent,
        launchScriptLoadedEvent,
        checkIntervalMs,
        timeoutDurationMs,
      });
    }
  }, [timeoutReached]);

  const sendHealthCheck = (statuses) => {
    const url = `${publicConfig?.personalizationHost}/profile/healthCheck?tag=al`;

    const queryParams = new URLSearchParams({
      checkIntervalMs: statuses.checkIntervalMs,
      timeoutDurationMs: statuses.timeoutDurationMs,
      scriptLoaded: statuses.scriptLoaded ? "true" : "false",
      scriptLoadedTime: statuses.scriptLoadedTime,
      scriptLoadedStatus: statuses.scriptLoadedStatus,
      satelliteExists: statuses.satelliteExists ? "true" : "false",
      launchLoadErrorEvent: statuses.launchLoadErrorEvent ? "true" : "false",
      launchScriptLoadedEvent: statuses.launchScriptLoadedEvent
        ? "true"
        : "false",
    }).toString();

    fetch(`${url}&${queryParams}`)
      .then((response) => {
        if (!response.ok) {
          console.error(
            "Response from tag monitor healthCheck endpoint was not ok"
          );
        }
      })
      .catch((error) => {
        console.error("Response from tag monitor healthCheck failed :", error);
      });
  };

  return null;
};

const useLaunchEventListeners = (timeoutDurationMs) => {
  const [launchLoadError, setLaunchLoadError] = useState(false);
  const [launchScriptLoaded, setLaunchScriptLoaded] = useState(false);
  const [timeoutReached, setTimeoutReached] = useState(false);
  const listenersAttached = useRef(true); // Flag to track listeners

  useEffect(() => {
    const handleLaunchLoadError = () => {
      setLaunchLoadError(true);
    };

    const handleLaunchScriptLoaded = () => {
      setLaunchScriptLoaded(true);
    };

    const removeEventListeners = () => {
      if (listenersAttached.current) {
        window.removeEventListener(
          "launchScriptLoadError",
          handleLaunchLoadError
        );
        document.body.removeEventListener(
          "launch-library-loaded",
          handleLaunchScriptLoaded
        );
        listenersAttached.current = false; // Mark listeners as removed
      }
    };

    // Add event listeners
    window.addEventListener("launchScriptLoadError", handleLaunchLoadError);
    document.body.addEventListener(
      "launch-library-loaded",
      handleLaunchScriptLoaded
    );

    const timeoutId = setTimeout(() => {
      setTimeoutReached(true); // Mark that the timeout has been reached
      removeEventListeners(); // Remove listeners after timeout
    }, timeoutDurationMs);

    // Cleanup on unmount
    return () => {
      clearTimeout(timeoutId);
      removeEventListeners(); // Remove listeners during cleanup
    };
  }, [timeoutDurationMs]);

  return { launchLoadError, launchScriptLoaded, timeoutReached };
};

export default useWithResourceReadyTest;
